/**
 * GridX - A grid component for arranging children in a grid.
 *
 * @author Koh Tong Seng <tongsengkoh@ifca.com.my>
 * @since 2023-07-12
 *
 * @param {Object} props - The component props
 * @param {React.ReactNode} [props.children] - The children to render
 * @param {string} [props.className] - The CSS class name to apply
 * @param {AlignItemsEnum} [props.crossAxisAlignment] - The cross axis alignment
 * @param {'row' | 'column'} props.direction - The grid direction
 * @param {boolean} [props.fullWidth] - Whether to take up the full width
 * @param {string} [props.gap] - The gap between children
 * @param {string} [props.gridTemplate] - The grid template
 * @param {string} [props.height] - The height of the component
 * @param {JustifyContentEnum} [props.mainAxisAlignment] - The main axis alignment
 * @param {OverflowEnum} [props.overflow] - The overflow strategy
 *
 * @returns {JSX.Element} The rendered component
 */

import React from 'react'
import { AlignItemsEnum, JustifyContentEnum, OverflowEnum } from './LayoutXenum'
import useLayoutStyle from './useLayoutStyle'

type GridXProps = {
  children?: React.ReactNode
  className?: string
  crossAxisAlignment?: AlignItemsEnum
  direction: 'row' | 'column'
  fullWidth?: boolean
  gap?: string
  gridTemplate: string
  height?: string
  mainAxisAlignment?: JustifyContentEnum
  overflow?: OverflowEnum
}

export const GridX = React.memo<GridXProps>(
  ({
    children,
    className,
    crossAxisAlignment,
    direction,
    fullWidth,
    gap,
    gridTemplate,
    height,
    mainAxisAlignment,
    overflow,
  }: GridXProps) => {
    const style = useLayoutStyle({
      display: 'grid',
      crossAxisAlignment,
      direction,
      fullWidth,
      gap,
      gridTemplate,
      height,
      mainAxisAlignment,
      overflow,
    })

    return <div className={className} style={style}>{children}</div>
  }
)
